import React from 'react';
import {
  Alert, Box, Button, ButtonGroup, Stack, Typography,
} from '@mui/material';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { useWorkspaceShape } from '../../hooks/useWorkspaceShape';
import { track } from '../../utils/analytics';
import { planName } from '../../utils/planNames';

/**
 * The two answers that decide how much of FlowGate is on screen, and the only place to change
 * them once the onboarding card is gone.
 *
 * Deliberately says what each answer turns on, in the words of the thing it turns on ("members
 * and roles", "environments"), rather than "advanced mode". Somebody looking for the screen
 * where they invite a colleague should be able to find the switch that brings it back by
 * reading it.
 */
const CHOICES = [
  {
    key: 'worksWithATeam',
    question: 'Will anyone else need their own login?',
    reveals: 'Saying yes adds members and roles, so you can invite people and say what each can do.',
    no: 'Just me',
    yes: 'Yes, my team',
  },
  {
    key: 'usesSeveralNumbers',
    question: 'How many WhatsApp numbers do you run?',
    reveals: 'Saying “several” adds organisations and environments, which keep them apart.',
    no: 'One',
    yes: 'Several',
  },
];

const HowYouWorkSettings = () => {
  const { preferences, updatePreferences } = usePreferences();
  const { canChange } = useWorkspaceShape();
  const { plan, limits } = useEntitlements();
  // Saying "several" shows the screens for keeping numbers apart; it does not buy a second
  // number. Better to say so here than to let somebody build an environment and meet a 402.
  const numbersCapped = Boolean(preferences.usesSeveralNumbers)
    && limits?.numbers != null && limits.numbers <= 1;

  const answer = (key, value) => {
    if (preferences[key] === value) return;
    updatePreferences({ [key]: value, workspaceSetupCompleted: true });
    track('workspace_shape_changed', { key, value, from: 'settings' });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>How you work</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        FlowGate shows the setup you actually use. Change these whenever that changes, nothing
        is deleted either way.
      </Typography>

      {!canChange && (
        <Alert severity="info" sx={{ mb: 3 }}>
          A demo runs as one person on one number. Create an account to work with a team or run
          several numbers.
        </Alert>
      )}

      {numbersCapped && canChange && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid="numbers-capped-note">
          {plan === 'FREE'
            ? 'Free has one environment with one WhatsApp number. Team adds environments, which can '
              + 'share that number; several numbers come with Business.'
            : `${planName(plan)} connects one WhatsApp number. The screens for running several are `
              + 'here, but a second number comes with Business.'}
        </Alert>
      )}

      <Stack spacing={3}>
        {CHOICES.map(({ key, question, reveals, no, yes }) => (
          <Box key={key}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{question}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {reveals}
            </Typography>
            <ButtonGroup size="small" disabled={!canChange} aria-label={question}>
              <Button
                variant={preferences[key] ? 'outlined' : 'contained'}
                onClick={() => answer(key, false)}
                data-testid={`${key}-no`}
              >
                {no}
              </Button>
              <Button
                variant={preferences[key] ? 'contained' : 'outlined'}
                onClick={() => answer(key, true)}
                data-testid={`${key}-yes`}
              >
                {yes}
              </Button>
            </ButtonGroup>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default HowYouWorkSettings;
